// Started from shadcn/ui (new-york-v4) "badge", restyled to Union (design system artifact:
// components/Chip, status chips). States a status in a word or two: lead with an icon,
// keep a word ("Checked in", "Held · 4:59"), never colour alone, at most one per row.

import { cn } from "@union/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

const badgeVariants = cva(
  [
    "inline-flex w-fit shrink-0 items-center gap-space-1 whitespace-nowrap rounded-pill border px-space-2 py-0.5 type-label",
    "[&>svg]:pointer-events-none [&>svg]:size-3.5 [&>svg]:[stroke-width:2]",
  ],
  {
    variants: {
      variant: {
        /** Neutral information. */
        neutral: "border-border bg-muted text-foreground",
        /** Confirmed, checked in, paid. */
        success: "border-transparent bg-success-soft text-success",
        /** Pending, held, waitlisted. */
        warning: "border-transparent bg-warning-soft text-warning",
        /** Cancelled, closed, failed. */
        destructive: "border-transparent bg-destructive-soft text-destructive",
        /** Yours (your booking, your group). */
        primary: "border-transparent bg-primary-soft text-primary-text",
        /** Events, lessons, social. */
        secondary: "border-transparent bg-secondary-soft text-secondary-text",
      },
    },
    defaultVariants: { variant: "neutral" },
  },
);

function Badge({
  className,
  variant = "neutral",
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";
  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
